import { DateTime } from "luxon";
import { useMemo } from "react";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { getWeekIndexOfSpecificDate, getWeekStart } from "@/utils/dateUtils";

export { getWeekIndexOfSpecificDate };

export type WeeksAndActiveState = {
	weekNumber: number;
	isActive: boolean;
	firstDate: DateTime;
	weekIndex: number;
};

export const useGetMasterScheduleWeeksAndActiveState = ({ firstDateToUse, weekStartDay }: { firstDateToUse?: DateTime; weekStartDay?: number }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const effectiveWeekStartDay = !isRuntimeWeekStartEnabled ? 0 : (weekStartDay ?? chosenDepartment.week_start_day);
	const firstDateOfCurrentWeek = useMemo(
		() =>
			firstDateToUse
				? getWeekStart(firstDateToUse.setZone(timezone), effectiveWeekStartDay)
				: getWeekStart(DateTime.now().setZone(timezone), effectiveWeekStartDay),
		[timezone, firstDateToUse, effectiveWeekStartDay],
	);
	const amountOfWeeks = useMemo(() => chosenDepartment.master_schedule_weeks ?? 2, [chosenDepartment.master_schedule_weeks]);

	/**
	 * 0 means week 1 is active, 1 means week 2 is active, etc.
	 */
	const activeMasterScheduleWeekIdx = useMemo(
		() =>
			getWeekIndexOfSpecificDate({
				chosenDepartmentFirstSunday: chosenDepartment.first_sunday,
				dateToCheck: firstDateOfCurrentWeek,
				masterScheduleWeeks: amountOfWeeks,
				timezone,
				weekStartDay: effectiveWeekStartDay,
			}),
		[firstDateOfCurrentWeek, chosenDepartment.first_sunday, amountOfWeeks, timezone, effectiveWeekStartDay],
	);
	return useMemo<WeeksAndActiveState[]>(() => {
		return Array.from({ length: amountOfWeeks }, (_, weekIndex) => ({
			isActive: weekIndex === activeMasterScheduleWeekIdx,
			weekNumber: weekIndex + 1,
			weekIndex: weekIndex,
			firstDate:
				weekIndex === activeMasterScheduleWeekIdx ? firstDateOfCurrentWeek : firstDateOfCurrentWeek.plus({ weeks: weekIndex + activeMasterScheduleWeekIdx }),
		}));
	}, [amountOfWeeks, activeMasterScheduleWeekIdx, firstDateOfCurrentWeek]);
};
